/**
 * The Offered-to-you screen's controller: `transferOffers.model.ts` wired to
 * `fetchTransferOffers` and `fetchCollections` for the reads and
 * `answerTransfer` for the one write, with thumbnails from
 * `useWorkThumbnails`.
 *
 * It owns what the model cannot: which collection each offer would land in,
 * which row is mid-flight and which way, and the round counter that re-reads
 * after an answer — a row does not change when answered, it leaves. The
 * collections are read alongside the offers because Accept is not honest
 * until there is somewhere to accept into.
 */

import { useCallback, useEffect, useState } from "react";
import type { CollectionSummary, TransferOffer } from "@ondo/shared";

import { answerTransfer, fetchCollections, fetchTransferOffers } from "../lib/api.ts";
import { useWorkThumbnails } from "../lib/useWorkThumbnails.ts";
import { OFFERS_COPY } from "./transfers.constants.ts";
import { describeOffersError, toOffersView } from "./transferOffers.model.ts";
import type { OffersView } from "./transferOffers.model.ts";

export interface TransferOffersController {
  view: OffersView;
  /** Which collection each offer would land in. Keyed by transfer. */
  chosen: ReadonlyMap<string, string>;
  choose: (transferId: string, collectionId: string) => void;
  /**
   * Which rows the reader has acknowledged the inherited covenants on. OND-85.
   *
   * Keyed by transfer rather than a single flag, because several offers are on
   * screen at once and an acknowledgement is about ONE picture's obligations.
   */
  acknowledged: ReadonlySet<string>;
  acknowledge: (transferId: string, next: boolean) => void;
  /**
   * Accept title, into the chosen collection, in the capacity the row names.
   *
   * `asOndoId` is the row's and not the controller's — OND-184. Each offer on
   * screen may be addressed to a different party, so the capacity belongs to
   * the card being pressed rather than to the screen.
   */
  accept: (transferId: string, asOndoId: string) => void;
  /** End the offer. There is no undo — see `declineNote`. */
  decline: (transferId: string) => void;
  /** Which row is mid-flight, and which way, so its controls can be disabled. */
  busy: { transferId: string; accepting: boolean } | null;
}

/**
 * Title offered to this account, and the two answers to it.
 *
 * TWO READS RATHER THAN ONE, and the second is not decoration: accepting
 * requires a collection the caller owns, so the picker has to be populated
 * before the control can be honest. A screen that offered Accept before the
 * collections landed would offer a button the server refuses.
 *
 * AFTER AN ANSWER IT RE-READS, the way every list in this app does. Accepting
 * moves the work into a collection and closes `ondo.transfer_offered_work_ids()`
 * in the same statement; declining closes it too. Either way the row does not
 * change — it leaves, and only Postgres knows what is left.
 *
 * @param workBase '/collector/me/work' — where a row links to.
 */
export function useTransferOffersController(
  workBase: string,
  selfOndoId: string,
): TransferOffersController {
  const [offers, setOffers] = useState<TransferOffer[] | null>(null);
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const images = useWorkThumbnails(offers, (each) => ({
    id: each.workId,
    imagePath: each.imagePath,
  }));
  const [chosen, setChosen] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState<{ transferId: string; accepting: boolean } | null>(
    null,
  );
  /** Bumped to re-read after an answer, so the list reflects Postgres. */
  const [round, setRound] = useState(0);

  useEffect(() => {
    let active = true;

    Promise.all([fetchTransferOffers(), fetchCollections()])
      .then(([found, mine]) => {
        if (!active) return;
        setOffers(found);
        setCollections(mine);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeOffersError(cause));
      });

    return () => {
      active = false;
    };
  }, [round]);

  const choose = useCallback((transferId: string, collectionId: string) => {
    setChosen((held) => new Map(held).set(transferId, collectionId));
  }, []);

  /**
   * Which rows the reader has acknowledged the inherited covenants on. OND-85.
   *
   * A set keyed by transfer rather than a boolean, for the reason `chosen` is a
   * Map: several offers are on screen at once and an acknowledgement is about
   * ONE picture's obligations. One flag would carry a tick from the row the
   * reader studied onto the row below it, which on this control is the whole
   * failure the ticket is about.
   *
   * Cleared on every re-read, because the set is rebuilt from rows that have
   * just arrived and a tick on a row that has gone is not an acknowledgement of
   * anything.
   */
  const [acknowledged, setAcknowledged] = useState<ReadonlySet<string>>(new Set());

  const acknowledge = useCallback((transferId: string, next: boolean) => {
    setAcknowledged((held) => {
      const copy = new Set(held);
      if (next) copy.add(transferId);
      else copy.delete(transferId);
      return copy;
    });
  }, []);

  const answer = useCallback(
    (
      transferId: string,
      accepting: boolean,
      collectionId: string | null,
      acknowledgeCovenants: boolean,
      asOndoId: string | null,
    ) => {
      // Stated here as well as gated in the view, because this is the half that
      // reaches the server. `answerTransferSchema` refuses an acceptance with no
      // collection, and a request sent to be refused would put a server error in
      // front of somebody who has simply not chosen yet.
      if (accepting && !collectionId) {
        setError(OFFERS_COPY.errors.needsCollection);
        return;
      }

      setBusy({ transferId, accepting });
      setError(null);

      answerTransfer(transferId, {
        accept: accepting,
        collectionId,
        acknowledgeCovenants,
        // OND-184. The capacity the row says this sale was made in, sent on
        // every acceptance rather than only when a firm is involved — so the
        // request states what the screen believes and the database refuses it
        // if the screen is reading a row that has since moved. Null on a
        // decline, where there is no capacity to refuse in.
        asOndoId,
      })
        .then(() => {
          setOffers(null);
          setCollections(null);
          setAcknowledged(new Set());
          setRound((n) => n + 1);
        })
        .catch((cause: unknown) => {
          setError(describeOffersError(cause));
        })
        .finally(() => {
          setBusy(null);
        });
    },
    [],
  );

  return {
    view: toOffersView({ offers, collections, error, images, workBase, selfOndoId }),
    chosen,
    choose,
    acknowledged,
    acknowledge,
    accept: useCallback(
      (transferId: string, asOndoId: string) => {
        answer(
          transferId,
          true,
          chosen.get(transferId) ?? null,
          acknowledged.has(transferId),
          asOndoId,
        );
      },
      [answer, chosen, acknowledged],
    ),
    // A DECLINE SENDS NO ACKNOWLEDGEMENT, and that is the server's rule as well
    // as this one: refusing a picture assumes nothing, so there is nothing to
    // have been shown.
    decline: useCallback(
      (transferId: string) => {
        answer(transferId, false, null, false, null);
      },
      [answer],
    ),
    busy,
  };
}
